import React, { useState } from 'react'
import { Alert, Skeleton, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { useSelector } from 'react-redux'
import i18next from 'i18next'
import { customReportSectionOptions } from '../../../../../features/customReport/queries/section/customReportSectionOptions'
import {
  customReportDateSelector,
  customReportArgumentsSelector
} from '../../../../../redux/context/customReports/selectors'
import { contextCompanyIdSelector } from '../../../../../redux/context/company/selectors'
import { appLanguageSelector } from '../../../../../redux/app/selectors'
import {
  CustomReportDataSection,
  CustomReportSectionsType
} from '../../../../../redux/context/customReports/typesSection'
import ReportSection from './CustomReportSection'

interface SectionDataLoaderProps {
  section: CustomReportSectionsType
  editModal: (section: CustomReportSectionsType) => void
}

/**
 * Merges the original section config (from report detail) with evaluated data (from section GET).
 * Keeps original `categories` (for editing) and sets `categoriesEvaluated` (for display).
 */
const mergeWithEvaluatedData = (
  configSection: CustomReportSectionsType,
  evaluatedData: CustomReportSectionsType
): CustomReportSectionsType => {
  if (!('categories' in evaluatedData)) return { ...configSection, ...evaluatedData }
  const dataSection = evaluatedData as CustomReportDataSection
  const categories = 'categories' in configSection ? (configSection as CustomReportDataSection).categories : []
  return {
    ...dataSection,
    categories,
    categoriesEvaluated: dataSection.categories
  } as CustomReportSectionsType
}

const SectionDataLoader: React.FC<SectionDataLoaderProps> = ({ section, editModal }) => {
  const [hasBeenInView, setHasBeenInView] = useState(false)
  const { ref } = useInView({
    rootMargin: '300px',
    onChange: nowInView => {
      if (nowInView && !hasBeenInView) setHasBeenInView(true)
    }
  })

  const companyId = useSelector(contextCompanyIdSelector)!
  const date = useSelector(customReportDateSelector)
  const language = useSelector(appLanguageSelector)
  const args = useSelector(customReportArgumentsSelector)

  const { data, error, isLoading, isFetching } = useQuery(
    customReportSectionOptions(companyId, section.reportId, section.id, { date, language, args }, hasBeenInView)
  )

  const evaluatedSection = data ? mergeWithEvaluatedData(section, data) : section
  const isRefetching = isFetching && !!data

  const renderContent = () => {
    if (!data && (isLoading || !hasBeenInView)) {
      return <Skeleton active style={{ padding: 16 }} />
    }

    return (
      <>
        {error && (
          <Alert
            type="error"
            showIcon
            message={i18next.t('notifications:error:sectionLoadFailed')}
            description={error?.message}
            style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1, margin: 0 }}
            banner
          />
        )}
        <ReportSection section={evaluatedSection} editModal={editModal} />
        {isRefetching && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          </div>
        )}
      </>
    )
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {renderContent()}
    </div>
  )
}

export default SectionDataLoader
